import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2"; // Import SweetAlert

function EditContactButton() {
  // Extract route parameters for account and contact IDs
  const { accountId, contactId } = useParams();
  const navigate = useNavigate();

  // State for form data with initial values
  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    middleInitial: "",
    lastName: "",
    email: "",
    role: "Dental Hygienist",
    eBilling: false,
  });

  // State for form validation errors
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
  });

  // State for loading and saving indicators
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Effect to fetch contact data when component mounts or contactId changes
  useEffect(() => {
    const fetchContact = async () => {
      // Skip if no contactId is provided
      if (!contactId) return;

      try {
        setIsLoading(true);
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/contacts/contact/${contactId}`);
        const contactData = response.data.data;

        // Populate form with fetched data
        setFormData({
          title: contactData.title || "",
          firstName: contactData.firstName || "",
          middleInitial: contactData.middleInitial || "",
          lastName: contactData.lastName || "",
          email: contactData.email || "",
          role: contactData.role || "Dental Hygienist",
          eBilling: contactData.eBilling || false,
        });
      } catch (error) {
        // Display error using SweetAlert
        Swal.fire({
          icon: 'error',
          title: 'Load Failed',
          text: 'Failed to load contact data. Please try again.',
          confirmButtonColor: '#004aad',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchContact();
  }, [contactId]);

  // Handle input changes for all form fields
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  // Function to show validation error using SweetAlert
  const showValidationError = () => {
    Swal.fire({
      icon: 'warning',
      title: 'Required Fields',
      text: 'First Name and Last Name are required!',
      confirmButtonColor: '#004aad',
    });
  };

  // Function to show success message using SweetAlert
  const showSuccessMessage = () => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: 'Contact updated successfully!',
      confirmButtonColor: '#004aad',
      timer: 2000,
      timerProgressBar: true,
    });
  };

  // Function to show error message using SweetAlert
  const showErrorMessage = () => {
    Swal.fire({
      icon: 'error',
      title: 'Update Failed',
      text: 'Failed to update contact. Please try again.',
      confirmButtonColor: '#004aad',
    });
  };

  // Function to confirm navigation on cancel
  const confirmCancel = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You have unsaved changes that will be lost.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#004aad',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, cancel',
      cancelButtonText: 'No, keep editing'
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(`/account/${accountId}/contacts`);
      }
    });
  };

  // Handle save button click
  const handleSaveClick = async () => {
    // Validate required fields
    const newErrors = {
      firstName: !formData.firstName,
      lastName: !formData.lastName,
    };

    setErrors(newErrors);

    // Show validation error if required fields are empty
    if (newErrors.firstName || newErrors.lastName) {
      showValidationError();
      return;
    }

    setIsSaving(true);
    try {
      // Send PUT request to update contact
      await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/contacts/${contactId}`,
        formData
      );

      // Show success message
      showSuccessMessage();
      
      // Navigate to account page after successful update
      setTimeout(() => {
        navigate(`/TabsWorking/${accountId}`);
      }, 2000);

    } catch (error) {
      // Show error message
      showErrorMessage();
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state while fetching data
  if (isLoading) {
    return <div>Loading contact data...</div>;
  }

  return (
    <>
      <div style={{ width: "95%", margin: "0 auto", padding: "0px" }}>
        <div className="row">
          <div className="col-md-10">
            {/* Page header */}
            <h3>{formData.firstName ? "Edit Contact" : "Edit Account Contact"}</h3>
            <br />
            <h4 style={{ color: "#1E90FF" }}>Provider Details</h4>
            
            {/* Form container */}
            <div
              style={{
                backgroundColor: "#f0f0f0",
                padding: "20px",
                borderTop: "3px solid #004aad",
              }}
            >
              <div
                style={{
                  padding: "20px",
                  borderRadius: "8px",
                  maxWidth: "600px",
                  marginLeft: "0",
                  marginRight: "auto",
                  fontFamily: "Arial, sans-serif",
                }}
              >
                {/* Title input field */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="title"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    id="title"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* First Name input field (required) */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="firstName"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    First Name<span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    id="firstName"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: errors.firstName
                        ? "1px solid red"
                        : "1px solid #ccc",
                      boxSizing: "border-box",
                    }}
                  />
                  {errors.firstName && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                      First name is required
                    </span>
                  )}
                </div>

                {/* Middle Initial input field */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="middleInitial"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Middle Initial
                  </label>
                  <input
                    type="text"
                    name="middleInitial"
                    value={formData.middleInitial}
                    onChange={handleInputChange}
                    id="middleInitial"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* Last Name input field (required) */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="lastName"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Last Name<span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    id="lastName"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: errors.lastName
                        ? "1px solid red"
                        : "1px solid #ccc",
                      boxSizing: "border-box",
                    }}
                  />
                  {errors.lastName && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                      Last name is required
                    </span>
                  )}
                </div>

                {/* Email input field */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="email"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    id="email"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* Role dropdown selection */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="role"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Role
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    id="role"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      boxSizing: "border-box",
                      height: "35px",
                    }}
                  >
                    <option value="Dental Hygienist">Dental Hygienist</option>
                    <option value="Dental Provider">Dental Provider</option>
                    <option value="Dental Student Provider">
                      Dental Student Provider
                    </option>
                    <option value="Dental Faculty Provider">
                      Dental Faculty Provider
                    </option>
                    <option value="Dental Assistant">Dental Assistant</option>
                    <option value="Clinic Director">Clinic Director</option>
                    <option value="Office Manager">Office Manager</option>
                    <option value="Front Desk">Front Desk</option>
                    <option value="BookKeeper">BookKeeper</option>
                    <option value="Practice Mini-Lab Technician">
                      Practice Mini-Lab Technician
                    </option>
                    <option value="Administrator">Administrator</option>
                  </select>
                </div>

                {/* E-Billing checkbox */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="eBilling"
                      checked={formData.eBilling}
                      onChange={handleInputChange}
                      style={{ marginRight: "8px" }}
                    />
                    E-Billing
                  </label>
                </div>

                {/* Save button */}
                <Button
                  style={{
                    backgroundColor: "#004aad",
                    borderColor: "#004aad",
                    color: "white",
                    padding: "10px 20px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                  onClick={handleSaveClick}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>

                {/* Cancel button with confirmation */}
                <Button
                  style={{
                    backgroundColor: "#6c757d",
                    borderColor: "#6c757d",
                    color: "white",
                    padding: "10px 20px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    marginTop: "10px",
                    marginLeft: "10px",
                  }}
                  onClick={confirmCancel}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <br />
      <br />
    </>
  );
}

export default EditContactButton;